import { useEffect, useState, useCallback } from 'react'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Portal } from '../portal'

interface ModalProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: React.ReactNode
  className?: string
}

/**
 * Reusable modal component with Portal, animations, and keyboard handling.
 * Use this as a base for any centered modal dialog.
 */
export function Modal({ isOpen, onClose, title, children, className }: ModalProps) {
  const [isClosing, setIsClosing] = useState(false)
  const [shouldRender, setShouldRender] = useState(false)

  const handleClose = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      setShouldRender(false)
      onClose()
    }, 200)
  }, [onClose])

  // Handle open/close state
  useEffect(() => {
    if (isOpen) {
      setShouldRender(true)
      setIsClosing(false)
    } else if (shouldRender) {
      // Trigger close animation when isOpen becomes false
      setIsClosing(true)
      const timeout = setTimeout(() => {
        setShouldRender(false)
      }, 200)
      return () => clearTimeout(timeout)
    }
  }, [isOpen, shouldRender])

  // Close on escape key & manage body scroll
  useEffect(() => {
    if (!isOpen) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose()
    }

    document.addEventListener('keydown', handleEscape)
    document.body.style.overflow = 'hidden'

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [isOpen, handleClose])

  if (!shouldRender) return null

  return (
    <Portal>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        {/* Backdrop */}
        <div
          className={cn(
            "fixed inset-0 bg-black/70 backdrop-blur-md transition-all duration-200",
            isClosing ? "opacity-0 backdrop-blur-none" : "opacity-100"
          )}
          onClick={handleClose}
        />

        {/* Modal wrapper for centering */}
        <div className="min-h-full flex items-center justify-center p-4 sm:p-8">
          {/* Modal content */}
          <div
            className={cn(
              "relative z-10 w-full max-w-2xl transition-all duration-200 ease-out flex flex-col",
              isClosing
                ? "opacity-0 scale-75 translate-y-4"
                : "opacity-100 scale-100 translate-y-0 animate-modal-in",
              className
            )}
          >
            {/* Header */}
            {title && (
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-medium text-white">{title}</h3>
                <button
                  onClick={handleClose}
                  className="p-1.5 rounded-lg text-[#888] hover:text-white hover:bg-white/10 transition-colors"
                >
                  <X className="size-4" />
                </button>
              </div>
            )}

            {/* Content */}
            {children}
          </div>
        </div>
      </div>
    </Portal>
  )
}
